import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, MessageSquareWarning, Sparkles, Image as ImageIcon, Target, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

type Campaign = {
  id: string;
  name: string;
  platform: string;
  status: string;
};

export const Route = createFileRoute("/_app/campaigns/$campaignId/approve")({
  component: ApprovalPage,
  head: () => ({
    meta: [
      { title: "Approve Ad · AdKavi" },
      { name: "description", content: "Review and approve your AI-generated ad before it's scheduled or published." },
    ],
  }),
});

function ApprovalPage() {
  const { campaignId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [changes, setChanges] = useState("");
  const [showChangeBox, setShowChangeBox] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("campaigns")
      .select("id, name, platform, status")
      .eq("id", campaignId)
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error(error);
        setCampaign((data as Campaign | null) ?? null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, campaignId]);

  const approve = async () => {
    if (!campaign || !user) return;
    setSubmitting(true);
    const { error } = await supabase
      .from("campaigns")
      .update({ status: "scheduled" })
      .eq("id", campaign.id)
      .eq("user_id", user.id);
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("approval.statusApproved"));
    navigate({ to: "/campaigns/$campaignId", params: { campaignId: campaign.id } });
  };

  const requestChanges = async () => {
    if (!campaign || !user) return;
    if (!changes.trim()) {
      toast.error(t("approval.changesPlaceholder"));
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("campaigns")
      .update({ status: "needs_changes" })
      .eq("id", campaign.id)
      .eq("user_id", user.id);
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("approval.statusChanges"));
    setShowChangeBox(false);
    setChanges("");
  };

  if (loading) {
    return (
      <section className="max-w-4xl mx-auto px-6 py-10 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> {t("approval.loading")}
      </section>
    );
  }

  if (!campaign) {
    return (
      <section className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-muted-foreground">{t("approval.notFound")}</p>
        <Button asChild variant="ghost" className="mt-4">
          <Link to="/campaigns"><ArrowLeft className="w-4 h-4 mr-2" /> {t("approval.backToCampaigns")}</Link>
        </Button>
      </section>
    );
  }

  return (
    <section className="max-w-4xl mx-auto px-6 py-8">
      <Link
        to="/campaigns/$campaignId"
        params={{ campaignId: campaign.id }}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> {t("approval.back")}
      </Link>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("approval.title")}</span>
          </h1>
          <p className="mt-1 text-muted-foreground">{campaign.name}</p>
        </div>
        <Badge variant="secondary" className="capitalize">{campaign.status}</Badge>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> {t("approval.copy")}
            </CardTitle>
            <CardDescription>{t("approval.copyDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-border/60 bg-card/50 p-4 space-y-3">
              <p className="text-lg font-semibold">{t("approval.headline")} {campaign.name}</p>
              <p className="text-sm text-muted-foreground">{t("approval.body")}</p>
              <Badge variant="outline">{t("approval.cta.learn")}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-primary" /> {t("approval.creative")}
            </CardTitle>
            <CardDescription>{t("approval.creativeDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="aspect-square rounded-lg border border-dashed border-border/60 bg-gradient-to-br from-primary/15 via-card to-secondary/15 flex items-center justify-center text-xs text-muted-foreground">
              {t("approval.creativePreview")}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" /> {t("approval.targeting")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3 text-sm">
              <Field label={t("approval.field.locations")} value="United States" />
              <Field label={t("approval.field.age")} value="18 – 65+" />
              <Field label={t("approval.field.gender")} value={t("approval.field.all")} />
              <Field label={t("approval.field.interests")} value="—" />
              <Field label={t("approval.field.language")} value={t("approval.field.english")} />
              <Field label={t("approval.field.platform")} value={campaign.platform} className="capitalize" />
            </div>
          </CardContent>
        </Card>
      </div>

      {showChangeBox && (
        <Card className="mt-6 border-amber-500/40 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-base">{t("approval.requestChanges")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={4}
              value={changes}
              onChange={(e) => setChanges(e.target.value)}
              placeholder={t("approval.changesPlaceholder")}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowChangeBox(false)}>{t("approval.cancel")}</Button>
              <Button size="sm" onClick={requestChanges} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t("approval.submit")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <Button variant="outline" onClick={() => setShowChangeBox((v) => !v)} disabled={submitting}>
          <MessageSquareWarning className="w-4 h-4 mr-2" /> {t("approval.requestChanges")}
        </Button>
        <Button onClick={approve} disabled={submitting} className="bg-orange-500 hover:bg-orange-600 text-white">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          {t("approval.approve")}
        </Button>
      </div>
    </section>
  );
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="rounded-lg border border-border/40 bg-background/30 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 font-medium ${className ?? ""}`}>{value}</p>
    </div>
  );
}
